import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { InputComponent } from 'src/app/components/shared/input';
import { CategoryService } from 'src/app/services/category';
import { LocaleService } from 'src/app/services/locale';
import { PlusComponent } from '../../../../icons/plus';
import { DropdownComponent } from '../../../../shared/dropdown';
import { TrashComponent } from '../../../../icons/trash';
import { CountryPickerComponent } from '../../../../shared/country-picker';
import { YearPickerComponent } from '../../../../shared/year-picker';
import { AngleDownComponent } from '../../../../icons/angle-down';
import { debounceTime, distinctUntilChanged } from 'rxjs';

interface Subcategory {
  id: string;
  label: string;
  itaLabel?: string; // obbligatoria
}

export interface MainCategory {
  name: string;
  itaName: string;
  subcategories: Subcategory[];
}

export interface Answer {
  main_category?: string; // <-- Add this line
  purchase_category?: string;
  spend_region?: string;
  money_amount?: number;
  money_unit?: string;
  spend_year?: number;
  note?: string;
}

interface Question {
  answers: Answer[] | null;
  emissions: any[];
  id: string;
  done: boolean;
  title: string;
  heading: string;
  doubt: string;
}

interface Category {
  questions: Question[];
  id: string;
  done: boolean;
  title: string;
  heading: string;
  doubt: string;
  emissions: any[];
}

@Component({
  selector: 'app-category4-question5',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    PlusComponent,
    DropdownComponent,
    TrashComponent,
    CountryPickerComponent,
    YearPickerComponent,
    AngleDownComponent
  ],
  template: `
    <div class="text-[12px] text-[#616161] mt-1">
      {{ strings[language].answer }}
    </div>

    <div class="flex flex-wrap gap-4 sm:gap-6 mt-4">
      <div
        (click)="toggleUsage(true)"
        class="cursor-pointer flex gap-2 items-center"
      >
        <div
          [class.bg-green-950]="usage"
          class="w-4 h-4 border rounded-full flex-shrink-0"
        ></div>
        <span>{{ strings[language].choice_yes }}</span>
      </div>
      <div
        (click)="toggleUsage(false)"
        class="cursor-pointer flex gap-2 items-center"
      >
        <div
          [class.bg-green-950]="!usage"
          class="w-4 h-4 border rounded-full flex-shrink-0"
        ></div>
        <span>{{ strings[language].choice_no }}</span>
      </div>
    </div>

    <!-- Purchase Table -->
    <div *ngIf="usage">
      <div class="flex justify-end mb-2 items-center">
        <div
          (click)="addRow()"
          class="bg-[#F8F9FA] px-3 py-2 rounded text-white text-md cursor-pointer"
        >
          <app-plus></app-plus>
        </div>
      </div>
      <form [formGroup]="form">
        <div class="space-y-4" formArrayName="purchases">
          <div
            *ngFor="let row of purchases.controls; let i = index"
            [formGroupName]="i"
            class="border rounded-lg p-4"
          >
            <div class="flex justify-end mb-4 cursor-pointer">
              <div
                class="w-1/12 flex justify-end items-center cursor-pointer"
                (click)="removeRow(i)"
              >
                <app-trash></app-trash>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium mb-1">
                  {{ strings[language].lable_1 }}
                </label>

                <div class="relative" #closeDropdown>
                  <!-- Dropdown trigger -->
                  <div
                    class="p-3 pr-10 rounded-lg border w-full border-gray-300 bg-white cursor-pointer hover:border-[#043B22] focus-within:ring-2 focus-within:ring-[#043B22] h-12 flex items-center relative"
                    (click)="toggleDropdown(i)"
                    [title]="getSelectedLabel(row)"
                  >
                    <span class="text-gray-800 block truncate w-full">
                      {{
                        getSelectedLabel(row) ||
                          strings[language].placeholder_label
                      }}
                    </span>
                    <app-angle-down
                      class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                    ></app-angle-down>
                  </div>

                  <!-- Dropdown content -->
                  <ul
                    *ngIf="dropdownOpenStates[i]"
                    class="absolute left-0 right-0 z-20 bg-white border border-gray-300 mt-2 max-h-64 overflow-auto shadow-xl rounded-lg scroll-smooth"
                  >
                    <!-- Search input -->
                    <li
                      class="sticky top-0 bg-white border-b border-gray-200 z-10"
                    >
                      <input
                        type="text"
                        [formControl]="categorySearchControl"
                        placeholder="{{ strings[language].lable_1 }}"
                        class="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#043B22]"
                        (click)="$event.stopPropagation()"
                      />
                    </li>

                    <!-- Categories and subcategories -->
                    <ng-container *ngFor="let category of filteredCategories">
                      <!-- Category title -->
                      <li
                        class="px-4 py-2 text-sm font-semibold text-[#043B22] bg-gray-100 sticky top-[42px] z-0"
                      >
                        {{
                          language === 'english'
                            ? category.name
                            : category.itaName
                        }}
                      </li>

                      <!-- Subcategory items -->
                      <li
                        *ngFor="let sub of category.subcategories"
                        class="px-4 py-2 text-sm text-gray-700 hover:bg-[#E6F2EC] hover:text-[#043B22] cursor-pointer transition-all truncate"
                        [title]="
                          language === 'english' ? sub.label : sub.itaLabel
                        "
                        (click)="
                          selectCategory(i, sub.id); $event.stopPropagation()
                        "
                      >
                        {{ language === 'english' ? sub.label : sub.itaLabel }}
                      </li>
                    </ng-container>
                  </ul>
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].lable_3
                }}</label>
                <app-country-picker
                  [control]="getFormControl(row, 'country')"
                ></app-country-picker>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].lable_4
                }}</label>
                <app-input
                  type="number"
                  [dollar]="false"
                  unit=""
                  placeholder="0.00"
                  [control]="getFormControl(row, 'spend')"
                ></app-input>
                <div
                  class="text-red-500"
                  *ngIf="getFormControl(row, 'spend').errors?.['min']"
                >
                  {{ strings[language].lable_4 }}
                  {{ strings[language].minValidation }}
                </div>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].lable_5
                }}</label>
                <app-dropdown
                  [options]="currencyOptions"
                  [control]="getFormControl(row, 'currency')"
                ></app-dropdown>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].lable_6
                }}</label>
                <app-year-picker
                  [control]="getFormControl(row, 'period')"
                ></app-year-picker>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].lable_7
                }}</label>
                <app-input
                  [dollar]="false"
                  unit=""
                  placeholder="{{ strings[language].Optional_note }}"
                  [control]="getFormControl(row, 'note')"
                ></app-input>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  `
})
export class Category4Question5Component implements OnInit {
  @ViewChild('closeDropdown') dropdownContainer!: ElementRef;
  @ViewChildren('closeDropdown') dropdownContainers!: QueryList<ElementRef>;
  language = inject(LocaleService).getLanguage();
  categoryService = inject(CategoryService);
  dropdownOpenStates: boolean[] = [];
  form!: FormGroup;
  usage = false;

  currencies = ['EUR', 'USD'];
  currencyOptions = this.currencies.map(c => ({ value: c, label: c }));

  strings: any = {
    english: {
      Optional_note: 'Optional note',
      answer: 'Please fill in the required data if yes to proceed.',
      choice_yes: 'Yes',
      choice_no: 'No',
      lable_1: 'Purchase category',
      lable_3: 'Country of service provider',
      lable_4: 'Net Spend',
      lable_5: 'Currency',
      lable_6: 'Period',
      lable_7: 'Note',
      placeholder_label: 'Select purchase category',
      minValidation: 'must be greater than 0'
    },
    italian: {
      Optional_note: 'Note facoltative',
      answer:
        'Si prega di inserire i dati richiesti se la risposta è sì per procedere.',
      choice_yes: 'Sì',
      choice_no: 'No',
      lable_1: 'Categoria di acquisto',
      lable_3: 'Paese del fornitore di servizi',
      lable_4: 'Spesa netta',
      lable_5: 'Valuta',
      lable_6: 'Periodo',
      lable_7: 'Nota',
      placeholder_label: 'Seleziona la categoria di acquisto',
      minValidation: 'deve essere maggiore di 0'
    }
  };

  PRODUCT_CATEGORIES: MainCategory[] = [
    {
      name: 'Financial & Insurance Services',
      itaName: 'Servizi finanziari e assicurativi',
      subcategories: [
        {
          id:
            'financial_services-type_financial_intermediation_services_except_insurance_pension_funding_services',
          label:
            'Financial intermediation services (excluding insurance and pension funding)',
          itaLabel:
            'Servizi di intermediazione finanziaria (esclusi assicurazioni e fondi pensione)'
        },
        {
          id:
            'financial_services-type_services_auxiliary_to_financial_intermediation',
          label: 'Auxiliary financial services',
          itaLabel: "Servizi ausiliari all'intermediazione finanziaria"
        },
        {
          id:
            'insurance-type_insurance_pension_funding_services_except_compulsory_social_security_services',
          label:
            'Insurance and pension funding services (excluding compulsory social security)',
          itaLabel:
            'Servizi assicurativi e fondi pensione (esclusa la sicurezza sociale obbligatoria)'
        }
      ]
    },
    {
      name: 'Professional & Business Services',
      itaName: 'Servizi professionali e aziendali',
      subcategories: [
        {
          id: 'professional_services-type_computer_related_services',
          label: 'Computer-related services',
          itaLabel: 'Servizi informatici'
        },
        {
          id: 'professional_services-type_other_business_services',
          label: 'Other business services',
          itaLabel: 'Altri servizi aziendali'
        },
        {
          id: 'professional_services-type_research_development_services',
          label: 'Research and development services',
          itaLabel: 'Servizi di ricerca e sviluppo'
        }
      ]
    },
    {
      name: 'Educational, Recreational & Membership Services',
      itaName: 'Servizi educativi, ricreativi e associativi',
      subcategories: [
        {
          id: 'consumer_goods-type_recreational_cultural_sporting_services',
          label: 'Recreational, cultural, and sporting services',
          itaLabel: 'Servizi ricreativi, culturali e sportivi'
        },
        {
          id: 'education-type_education_services',
          label: 'Education services',
          itaLabel: 'Servizi educativi'
        },
        {
          id:
            'organizational_activity-type_membership_organisation_services_not_elsewhere_specified',
          label: 'Membership organisation services (not elsewhere specified)',
          itaLabel:
            'Servizi di organizzazioni associative (non classificati altrove)'
        }
      ]
    },
    {
      name: 'Other Not Elsewhere Specified',
      itaName: 'Altri servizi non altrove classificati',
      subcategories: [
        {
          id:
            'organizational_activity-type_other_services_not_elsewhere_specified',
          label: 'Other services (not elsewhere specified)',
          itaLabel: 'Altri servizi (non classificati altrove)'
        }
      ]
    },
    {
      name: 'Hospitality & Communications',
      itaName: 'Servizi di ospitalità e comunicazione',
      subcategories: [
        {
          id: 'restaurants_accommodation-type_hotel_restaurant_services',
          label: 'Hotel and restaurant services',
          itaLabel: 'Servizi alberghieri e di ristorazione'
        },
        {
          id: 'communication_services-type_post_telecommunication',
          label: 'Postal and telecommunication services',
          itaLabel: 'Servizi postali e di telecomunicazione'
        }
      ]
    },
    {
      name: 'Real estate services',
      itaName: 'Servizi immobiliari',
      subcategories: [
        {
          id: 'real_estate-type_real_estate_services',
          label: 'Real estate services',
          itaLabel: 'Servizi immobiliari'
        }
      ]
    },
    {
      name: 'Wholesale trade',
      itaName: "Commercio all'ingrosso",
      subcategories: [
        {
          id:
            'wholesale_trade-type_wholesale_trade_commission_trade_services_except_of_motor_vehicles_motorcycles',
          label: 'Wholesale trade (excluding motor vehicles and motorcycles)',
          itaLabel:
            "Commercio all'ingrosso (esclusi veicoli a motore e motocicli)"
        }
      ]
    },
    {
      name: 'Domestic services',
      itaName: 'Servizi domestici',
      subcategories: [
        {
          id: 'domestic_services-type_private_households_with_employed_persons',
          label: 'Private households with employed persons',
          itaLabel: 'Famiglie private con personale domestico'
        }
      ]
    },
    {
      name: 'Transport services',
      itaName: 'Servizi di trasporto',
      subcategories: [
        {
          id:
            'transport_services-type_supporting_auxiliary_and_travel_agency_services',
          label: 'Supporting, auxiliary and travel agency services',
          itaLabel: 'Servizi di supporto, ausiliari e agenzie di viaggio'
        }
      ]
    }
  ];

  constructor(private fb: FormBuilder) {}
  // Inside your Angular Component:

  categorySearchControl = new FormControl('');
  filteredCategories: any[] = [];
  ngOnInit(): void {
    this.form = this.fb.group({
      purchases: this.fb.array([])
    });
    this.filteredCategories = [...this.PRODUCT_CATEGORIES];

    // Setup search filter
    this.categorySearchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchTerm: any) => {
        this.filterCategories(searchTerm);
      });

    const categoriesRaw = localStorage.getItem('categories');
    let categories: Category[] = [];

    if (categoriesRaw) {
      try {
        categories = JSON.parse(categoriesRaw) as Category[];

        while (categories.length < 4) {
          categories.push(this.createDefaultCategory());
        }

        if (!categories[3].questions) categories[3].questions = [];
        if (categories[3].questions.length === 0) {
          categories[3].questions.push(this.createDefaultQuestion());
        }

        const answers = categories[3].questions[4].answers;
        if (answers && Array.isArray(answers)) {
          this.usage = true;
          answers.forEach((ans, index) => {
            const group = this.fb.group({
              mainCategory: [ans.main_category || '', Validators.required],
              country: [ans.spend_region || '', Validators.required],
              spend: [
                ans.money_amount || '',
                [Validators.required, Validators.min(0.01)]
              ],
              currency: [ans.money_unit || 'EUR', Validators.required],
              period: [ans.spend_year || '', Validators.required],
              note: [ans.note || '']
            });

            this.purchases.push(group);
            this.dropdownOpenStates.push(false); // Initialize dropdown state for this row
          });
        }
      } catch (e) {
        console.error('Error parsing localStorage categories:', e);
      }
    }

    // Initialize dropdown states array if empty
    if (this.dropdownOpenStates.length === 0 && this.purchases.length > 0) {
      this.dropdownOpenStates = new Array(this.purchases.length).fill(false);
    }

    this.purchases.valueChanges.subscribe(() => {
      this.saveToLocalStorage();
    });
  }

  get purchases(): FormArray {
    return this.form.get('purchases') as FormArray;
  }

  addRow(): void {
    const group = this.fb.group({
      mainCategory: ['', Validators.required],
      country: ['', Validators.required],
      spend: ['', [Validators.required, Validators.min(0.01)]],
      currency: ['EUR', Validators.required],
      period: ['', Validators.required],
      note: ['']
    });
    this.purchases.push(group);
    this.dropdownOpenStates.push(false);
  }

  removeRow(index: number): void {
    this.purchases.removeAt(index);
    this.dropdownOpenStates.splice(index, 1); // Remove corresponding state
  }

  toggleUsage(value: boolean): void {
    this.usage = value;
    if (value && this.purchases.length === 0) {
      this.addRow();
    }
    if (!value) {
      this.purchases.clear();
      this.saveToLocalStorage();
    }
  }

  getFormControl(row: AbstractControl, controlName: string): FormControl {
    return row.get(controlName) as FormControl;
  }

  saveToLocalStorage(): void {
    const purchasesData: Answer[] = this.purchases.controls.map(group => {
      const g = group.value;
      const selectedId = g.mainCategory;
      let mainCategoryInfo = null;
      let subcategoryInfo: Subcategory | null = null;

      for (const category of this.PRODUCT_CATEGORIES) {
        const foundSub = category.subcategories.find(
          sub => sub.id === selectedId
        );
        if (foundSub) {
          mainCategoryInfo = {
            name: category.name,
            itaName: category.itaName
          };
          subcategoryInfo = foundSub;
          break;
        }
      }

      return {
        main_category: g.mainCategory,
        main_category_info: mainCategoryInfo,
        purchase_category_info: subcategoryInfo,
        spend_region: g.country,
        money_amount: g.spend,
        money_unit: g.currency,
        spend_year: g.period,
        note: g.note
      };
    });

    let categories =
      ((this.categoryService.categories() as unknown) as Category[]) || [];

    while (categories.length < 4) {
      categories.push(this.createDefaultCategory());
    }

    if (!categories[3].questions) categories[3].questions = [];
    if (categories[3].questions.length === 0) {
      categories[3].questions.push(this.createDefaultQuestion());
    }

    categories[3].questions[4].answers = this.usage ? purchasesData : null;
    this.categoryService.persistCategories(categories);
  }

  private createDefaultCategory(): Category {
    return {
      id: '',
      done: false,
      title: '',
      heading: '',
      doubt: '',
      emissions: [],
      questions: [this.createDefaultQuestion()]
    };
  }

  private createDefaultQuestion(): Question {
    return {
      id: '',
      done: false,
      title: '',
      heading: '',
      doubt: '',
      emissions: [],
      answers: null
    };
  }
  toggleDropdown(index: number) {
    // Toggle current row and close others
    this.dropdownOpenStates = this.dropdownOpenStates.map((state, i) =>
      i === index ? !state : false
    );
  }
  selectCategory(index: number, subcategoryId: string) {
    const row = this.purchases.at(index);
    this.getFormControl(row, 'mainCategory').setValue(subcategoryId);
    this.dropdownOpenStates[index] = false; // Close after selection
  }

  getSelectedLabel(row: any): string | null {
    const selectedId = this.getFormControl(row, 'mainCategory').value;
    for (const category of this.PRODUCT_CATEGORIES) {
      const found = category.subcategories.find(sub => sub.id === selectedId);
      if (found) {
        const label =
          this.language === 'english' ? found.label : found.itaLabel;
        return label ?? null;
      }
    }
    return null;
  }
  filterCategories(searchTerm: string) {
    if (!searchTerm) {
      this.filteredCategories = [...this.PRODUCT_CATEGORIES];
      return;
    }

    const term = searchTerm.toLowerCase();
    this.filteredCategories = this.PRODUCT_CATEGORIES.map(category => {
      // Filter subcategories first
      const filteredSubs = category.subcategories.filter(
        sub =>
          sub.label.toLowerCase().includes(term) ||
          sub.id.toLowerCase().includes(term)
      );

      // Only include category if it matches search or has matching subcategories
      if (
        category.name.toLowerCase().includes(term) ||
        filteredSubs.length > 0
      ) {
        return {
          ...category,
          subcategories: filteredSubs
        };
      }
      return null;
    }).filter(category => category !== null);
  }
  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    // Check if click was inside any dropdown container
    const clickedInside = this.dropdownContainers.some((container: any) =>
      container.nativeElement.contains(event.target)
    );

    if (!clickedInside) {
      this.dropdownOpenStates = this.dropdownOpenStates.map(() => false);
    }
  }
}
